import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api/api'
import { useTypedSelector } from '../hooks/useTypedSelector'
import { EServices, TableHelper } from '../types/tableHelper'
import logger from '../utils/logger'
import styles from './css/StatusPage.module.css'

// Названия игр — ключи из эндпоинта /api/home/updated-times
type GameKey = 'CS2' | 'Dota2' | 'Tf2' | 'Rust'

interface UpdatedTimesResponse {
	CS2?: Record<string, number>
	Dota2?: Record<string, number>
	Tf2?: Record<string, number>
	Rust?: Record<string, number>
}

// Как мапить игру в картинку и человеческое имя.
const GAME_META: Record<GameKey, { pretty: string; icon: string }> = {
	CS2: { pretty: 'CS2', icon: 'Resources/Games/csgo.jpg' },
	Dota2: { pretty: 'Dota 2', icon: 'Resources/Games/dota2.jpg' },
	Tf2: { pretty: 'TF2', icon: 'Resources/Games/tf2.jpg' },
	Rust: { pretty: 'Rust', icon: 'Resources/Games/rust.jpg' },
}

const GAME_ORDER: GameKey[] = ['CS2', 'Dota2', 'Tf2', 'Rust']

type HealthStatus = 'ok' | 'degraded' | 'down'

// Пороги в секундах: 2 часа / 24 часа.
const THRESHOLD_OK_SEC = 2 * 60 * 60
const THRESHOLD_DEGRADED_SEC = 24 * 60 * 60

// Один цикл автообновления.
const AUTO_REFRESH_INTERVAL_MS = 60_000

const classifyStatus = (updatedUnixSec: number, nowUnixSec: number): HealthStatus => {
	if (!updatedUnixSec) return 'down'
	const age = nowUnixSec - updatedUnixSec
	if (age < THRESHOLD_OK_SEC) return 'ok'
	if (age < THRESHOLD_DEGRADED_SEC) return 'degraded'
	return 'down'
}

const formatRelativeTime = (
	updatedUnixSec: number,
	nowUnixSec: number,
	lang: {
		StatusPage_NeverUpdated: string
		StatusPage_TimeJustNow: string
		StatusPage_TimeMinutesAgo: string
		StatusPage_TimeHoursAgo: string
		StatusPage_TimeDaysAgo: string
	},
): string => {
	if (!updatedUnixSec) return lang.StatusPage_NeverUpdated

	const ageSec = Math.max(0, nowUnixSec - updatedUnixSec)
	if (ageSec < 60) return lang.StatusPage_TimeJustNow
	if (ageSec < 3600) return `${Math.floor(ageSec / 60)} ${lang.StatusPage_TimeMinutesAgo}`
	if (ageSec < 86_400) return `${Math.floor(ageSec / 3600)} ${lang.StatusPage_TimeHoursAgo}`
	return `${Math.floor(ageSec / 86_400)} ${lang.StatusPage_TimeDaysAgo}`
}

// Пытаемся достать EServices enum value по строковому ключу из ответа API.
// Если сервис появился на бэке, а во фронтовом enum его нет — возвращаем null,
// и отрисовываем как есть, без иконки.
const parseServiceKey = (name: string): EServices | null => {
	const value = (EServices as unknown as Record<string, number>)[name]
	return typeof value === 'number' ? (value as EServices) : null
}

const StatusPage: FC = () => {
	const {
		lang: { Value: lang },
	} = useTypedSelector(x => x.lang)

	const [data, setData] = useState<UpdatedTimesResponse | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [initialLoading, setInitialLoading] = useState(true)
	const [filter, setFilter] = useState<'all' | 'problems'>('all')
	const [autoRefresh, setAutoRefresh] = useState(true)
	// now используется для пересчёта «сколько времени прошло» — тикает раз в 30с.
	const [now, setNow] = useState(() => Math.floor(Date.now() / 1000))

	const abortRef = useRef<AbortController | null>(null)
	// Храним актуальный lang в ref, чтобы fetchData в setInterval не замыкался
	// на устаревшую версию при переключении RU/EN.
	const langRef = useRef(lang)
	useEffect(() => {
		langRef.current = lang
	}, [lang])

	// Стабильная ссылка на fetchData: не пересоздаётся при смене lang.
	const fetchDataRef = useRef<() => Promise<void>>()
	fetchDataRef.current = async () => {
		abortRef.current?.abort()
		const controller = new AbortController()
		abortRef.current = controller

		try {
			const response = await api.get<UpdatedTimesResponse>('home/updated-times', {
				signal: controller.signal,
			})
			setData(response.data)
			setError(null)
		} catch (err) {
			if (controller.signal.aborted) return
			logger.error('StatusPage fetch failed:', err)
			setError(langRef.current.StatusPage_ReloadError)
		} finally {
			if (!controller.signal.aborted) setInitialLoading(false)
		}
	}

	useEffect(() => {
		document.title = 'vvTable - Status'
		fetchDataRef.current?.()
		return () => abortRef.current?.abort()
	}, [])

	// Авто-обновление данных + локальный таймер для "X минут назад".
	useEffect(() => {
		if (!autoRefresh) return

		const dataInterval = window.setInterval(() => fetchDataRef.current?.(), AUTO_REFRESH_INTERVAL_MS)
		const nowInterval = window.setInterval(() => setNow(Math.floor(Date.now() / 1000)), 30_000)

		return () => {
			window.clearInterval(dataInterval)
			window.clearInterval(nowInterval)
		}
	}, [autoRefresh])

	// Агрегация по играм + суммарная статистика.
	const aggregated = useMemo(() => {
		if (!data) return null

		const byGame = GAME_ORDER.map(gameKey => {
			const services = data[gameKey] ?? {}
			const items = Object.entries(services).map(([name, ts]) => ({
				name,
				enumValue: parseServiceKey(name),
				updated: ts,
				status: classifyStatus(ts, now),
			}))
				// Закрытые сервисы (BitSkins) не показываем пользователям на статус-странице —
				// иначе висят красной плашкой «не работает» и смущают. Неизвестные (enumValue null)
				// оставляем как есть.
				.filter(item => item.enumValue === null || !TableHelper.CheckServiceIsHidden(item.enumValue))

			items.sort((a, b) => {
				// Сначала зелёные, потом жёлтые, потом красные.
				// Внутри одного статуса — свежие сверху (больший timestamp первым).
				const weight: Record<HealthStatus, number> = { ok: 0, degraded: 1, down: 2 }
				const byStatus = weight[a.status] - weight[b.status]
				if (byStatus !== 0) return byStatus
				if (a.updated !== b.updated) return b.updated - a.updated
				return a.name.localeCompare(b.name)
			})

			const counts = items.reduce(
				(acc, item) => {
					acc[item.status] += 1
					return acc
				},
				{ ok: 0, degraded: 0, down: 0 } as Record<HealthStatus, number>,
			)

			return { gameKey, items, counts }
		})

		const totals = byGame.reduce(
			(acc, g) => {
				acc.ok += g.counts.ok
				acc.degraded += g.counts.degraded
				acc.down += g.counts.down
				return acc
			},
			{ ok: 0, degraded: 0, down: 0 },
		)

		return { byGame, totals, total: totals.ok + totals.degraded + totals.down }
	}, [data, now])

	if (initialLoading) {
		return (
			<div className={styles.page}>
				<div className={styles.loader}>
					<i className='demo-icon icon-spin6 animate-spin' aria-hidden='true' />
				</div>
			</div>
		)
	}

	if (error && !aggregated) {
		return (
			<div className={styles.page}>
				<div className={styles.error} role='alert'>
					{error}
				</div>
			</div>
		)
	}

	if (!aggregated) return null

	return (
		<div className={styles.page}>
			<div className={styles.header}>
				<h1 className={styles.title}>{lang.StatusPage_Title}</h1>
				<div className={styles.subtitle}>{lang.StatusPage_Subtitle}</div>
			</div>

			<div className={styles.summary}>
				<div className={styles.summaryCard}>
					<span className={styles.summaryValue}>{aggregated.total}</span>
					<span className={styles.summaryLabel}>{lang.StatusPage_TotalServices}</span>
				</div>
				<div className={styles.summaryCard}>
					<span className={`${styles.summaryValue} ${styles.summaryValueOk}`}>{aggregated.totals.ok}</span>
					<span className={styles.summaryLabel}>{lang.StatusPage_StatusOk}</span>
				</div>
				<div className={styles.summaryCard}>
					<span className={`${styles.summaryValue} ${styles.summaryValueWarn}`}>{aggregated.totals.degraded}</span>
					<span className={styles.summaryLabel}>{lang.StatusPage_StatusDegraded}</span>
				</div>
				<div className={styles.summaryCard}>
					<span className={`${styles.summaryValue} ${styles.summaryValueDown}`}>{aggregated.totals.down}</span>
					<span className={styles.summaryLabel}>{lang.StatusPage_StatusDown}</span>
				</div>
			</div>

			<div className={styles.controls}>
				<div className={styles.filterGroup} role='tablist' aria-label='Filter'>
					<button
						type='button'
						role='tab'
						aria-selected={filter === 'all'}
						className={`${styles.filterBtn} ${filter === 'all' ? styles.filterBtnActive : ''}`}
						onClick={() => setFilter('all')}
					>
						{lang.StatusPage_FilterAll}
					</button>
					<button
						type='button'
						role='tab'
						aria-selected={filter === 'problems'}
						className={`${styles.filterBtn} ${filter === 'problems' ? styles.filterBtnActive : ''}`}
						onClick={() => setFilter('problems')}
					>
						{lang.StatusPage_FilterProblems}
					</button>
				</div>

				<label className={styles.autoRefresh}>
					<input
						type='checkbox'
						checked={autoRefresh}
						onChange={e => setAutoRefresh(e.target.checked)}
					/>
					{lang.StatusPage_AutoRefresh}
				</label>
			</div>

			<div className={styles.legend} aria-label={lang.StatusPage_Legend}>
				<span className={styles.legendItem}>
					<span className={`${styles.legendDot} ${styles.legendDotOk}`} />
					{lang.StatusPage_LegendOk}
				</span>
				<span className={styles.legendItem}>
					<span className={`${styles.legendDot} ${styles.legendDotWarn}`} />
					{lang.StatusPage_LegendDegraded}
				</span>
				<span className={styles.legendItem}>
					<span className={`${styles.legendDot} ${styles.legendDotDown}`} />
					{lang.StatusPage_LegendDown}
				</span>
			</div>

			<div className={styles.games}>
				{aggregated.byGame.map(({ gameKey, items, counts }) => {
					const visibleItems = filter === 'problems'
						? items.filter(i => i.status !== 'ok')
						: items

					const meta = GAME_META[gameKey]

					return (
						<section key={gameKey} aria-label={meta.pretty}>
							<div className={styles.gameHeader}>
								<img src={meta.icon} alt={meta.pretty} className={styles.gameIcon} />
								<h2 className={styles.gameName}>{meta.pretty}</h2>
								<div className={styles.gameStats}>
									<span
										className={`${styles.gameStat} ${counts.ok === 0 ? styles.gameStatZero : ''}`}
										title={lang.StatusPage_StatusOk}
									>
										<span className={`${styles.gameStatDot} ${styles.gameStatDotOk}`} />
										{counts.ok}
									</span>
									<span
										className={`${styles.gameStat} ${counts.degraded === 0 ? styles.gameStatZero : ''}`}
										title={lang.StatusPage_StatusDegraded}
									>
										<span className={`${styles.gameStatDot} ${styles.gameStatDotWarn}`} />
										{counts.degraded}
									</span>
									<span
										className={`${styles.gameStat} ${counts.down === 0 ? styles.gameStatZero : ''}`}
										title={lang.StatusPage_StatusDown}
									>
										<span className={`${styles.gameStatDot} ${styles.gameStatDotDown}`} />
										{counts.down}
									</span>
								</div>
							</div>

							{visibleItems.length === 0 ? (
								<div className={styles.emptyGame}>—</div>
							) : (
								<div className={styles.services}>
									{visibleItems.map(item => {
										const iconSrc = item.enumValue !== null
											? `Resources/ServicesIcons/${EServices[item.enumValue].toLowerCase()}.png`
											: null
										const displayName = item.enumValue !== null
											? TableHelper.GetServiceName(item.enumValue)
											: item.name
										// Статическое соответствие, без динамического доступа к styles[key].
										const dotClass =
											item.status === 'ok' ? styles.statusDotOk
												: item.status === 'degraded' ? styles.statusDotWarn
													: styles.statusDotDown
										const statusLabel =
											item.status === 'ok' ? lang.StatusPage_StatusOk
												: item.status === 'degraded' ? lang.StatusPage_StatusDegraded
													: lang.StatusPage_StatusDown

										return (
											<div
												key={item.name}
												className={styles.service}
												title={`${displayName} — ${statusLabel}`}
											>
												{iconSrc ? (
													<img
														src={iconSrc}
														alt=''
														className={styles.serviceIcon}
														onError={e => {
															(e.currentTarget as HTMLImageElement).style.visibility = 'hidden'
														}}
													/>
												) : (
													<span className={styles.serviceIconPlaceholder} aria-hidden='true' />
												)}
												<div className={styles.serviceBody}>
													<div className={styles.serviceName}>{displayName}</div>
													<div className={styles.serviceTime}>
														{formatRelativeTime(item.updated, now, lang)}
													</div>
												</div>
												<span
													className={`${styles.statusDot} ${dotClass}`}
													aria-label={statusLabel}
												/>
											</div>
										)
									})}
								</div>
							)}
						</section>
					)
				})}
			</div>
		</div>
	)
}

export default StatusPage
